import React, { useState } from 'react';
import { Shield, AlertTriangle, Eye, Mic, Users, Database, Smartphone, Lock, Info, X, User, Fingerprint, CreditCard, Dna, Wifi, Search, Target, Zap, Coins } from 'lucide-react';

const WELCOME_KEY = 'ai-security-hub-visited-v1';

const AiSecurityTool = () => {
  const [activeCategory, setActiveCategory] = useState('deepfakes');
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState(null);
  const [showWelcome, setShowWelcome] = useState(() => {
    try { return !localStorage.getItem(WELCOME_KEY); } catch { return true; }
  });

  const dismissWelcome = () => {
    try { localStorage.setItem(WELCOME_KEY, '1'); } catch {}
    setShowWelcome(false);
  };

  const categories = [
    {
      id: 'deepfakes',
      name: 'Deepfakes',
      icon: Eye,
      color: 'from-purple-500 to-pink-500',
      description: 'AI-generated fake videos and images',
      risks: [
        {
          summary: 'Fake compromising videos for blackmail',
          details: 'Attackers use AI to create realistic fake videos of you in compromising situations, then demand money to prevent their release. These videos can be created from just a few photos from your social media profiles and are becoming increasingly difficult to distinguish from real footage.'
        },
        {
          summary: 'Political misinformation campaigns',
          details: 'Deepfake technology is weaponized to create fake speeches or statements from political figures, spreading false information that can influence elections, damage reputations, or create social unrest. These videos spread rapidly on social media before they can be fact-checked.'
        },
        {
          summary: 'Identity impersonation for fraud',
          details: 'Criminals create deepfake videos to impersonate you during video calls with banks, employers, or family members to gain access to accounts, approve transactions, or obtain sensitive information. This bypasses traditional identity verification methods that rely on visual confirmation.'
        },
        {
          summary: 'Revenge porn and harassment',
          details: 'Malicious individuals create explicit deepfake content using your likeness without consent, often targeting women, public figures, or ex-partners. This causes severe psychological harm and can damage personal and professional relationships permanently.'
        },
        {
          summary: 'Corporate executive impersonation in video calls',
          details: 'Cybercriminals target businesses by creating deepfake videos of CEOs or executives to authorize fraudulent wire transfers, share confidential information, or manipulate employees during video conferences. These attacks can result in millions in losses.'
        }
      ],
      protection: [
        {
          summary: 'Verify video calls with pre-agreed code words',
          details: 'Establish secret phrases or questions with family and colleagues that only they would know. Before discussing sensitive matters or following unusual requests in video calls, ask for the code word. This simple verification can instantly expose deepfake impersonation attempts.'
        },
        {
          summary: 'Look for unnatural eye movements and lighting',
          details: 'Current deepfake technology often struggles with consistent eye contact, blinking patterns, and lighting consistency. Watch for eyes that don\'t track naturally, mismatched lighting on the face versus background, or facial expressions that seem delayed or unnatural.'
        },
        {
          summary: 'Check source credibility before sharing',
          details: 'Always verify controversial or shocking video content through multiple reputable news sources before sharing. Look for official statements, check the original posting source, and search for fact-checking reports. Misinformation spreads faster than corrections.'
        },
        {
          summary: 'Use deepfake detection tools',
          details: 'Utilize AI-powered detection services like TrueMedia.org, Hive Moderation, Sensity AI, or Deepware Scanner that can analyze videos for signs of manipulation. These tools examine pixel patterns, temporal inconsistencies, and other technical markers humans miss — no single tool is definitive, so cross-check with two or more.'
        },
        {
          summary: 'Watermark your authentic content',
          details: 'Add cryptographic provenance to your photos and videos using Content Credentials — the open C2PA standard supported by Adobe, Microsoft, and major camera makers. Tools like Adobe\'s Content Authenticity web app and Leica\'s Content Credentials cameras embed tamper-evident metadata that proves what\'s real versus manipulated in legal or professional contexts.'
        },
        {
          summary: 'Record baseline videos/photos for comparison',
          details: 'Create a library of authenticated photos and videos of yourself in various lighting conditions and angles. Store these securely and share them with trusted contacts so they can compare suspicious content against known authentic versions of your appearance.'
        }
      ]
    },
    {
      id: 'voice-cloning',
      name: 'Voice Cloning',
      icon: Mic,
      color: 'from-blue-500 to-cyan-500',
      description: 'AI replication of voices from audio samples',
      risks: [
        {
          summary: 'Fake emergency calls from "family members"',
          details: 'Scammers use AI to clone your loved one\'s voice from social media videos or voicemails, then call claiming to be in trouble and needing money immediately. These calls are emotionally manipulative and often target elderly family members who are more likely to send money without verification.'
        },
        {
          summary: 'Business email compromise via phone',
          details: 'Cybercriminals clone executive voices to call employees and authorize fraudulent wire transfers, change banking information, or access sensitive systems. These attacks bypass email security by using voice calls that seem to come from trusted leadership.'
        },
        {
          summary: 'Kidnapping hoaxes for ransom',
          details: 'Attackers call family members using a cloned voice of their loved one, playing fake sounds of distress and demanding ransom money. The emotional trauma and urgency make victims act without thinking, even when the person is actually safe.'
        },
        {
          summary: 'Voice authentication bypass',
          details: 'Many banking and customer service systems use voice recognition for identity verification. Criminals can use cloned voices to access your accounts, change passwords, or authorize transactions by fooling these automated voice authentication systems.'
        },
        {
          summary: 'AI voice in fake customer service calls',
          details: 'Scammers use cloned voices of trusted company representatives to call customers, claiming there are account issues that require immediate verification of personal information or payment details. These calls seem legitimate because the voice matches previous interactions.'
        },
        {
          summary: 'Elderly targeting with grandchild voice scams',
          details: 'A particularly cruel scam where criminals clone grandchildren\'s voices from social media to call grandparents claiming to be in jail, in an accident, or in trouble abroad. The emotional manipulation and age-related hearing changes make elderly victims especially vulnerable.'
        }
      ],
      protection: [
        {
          summary: 'Create family safe words for emergencies',
          details: 'Establish secret code words or phrases with family members that only you know. In any emergency call requesting money or help, ask for the safe word. Train all family members, especially elderly relatives, to always ask for this verification before taking action.'
        },
        {
          summary: 'Always verify unusual requests via second channel',
          details: 'If someone calls with an urgent request, hang up and call them back on a known number, send a text message, or contact them through a different method to verify the request is legitimate. Never rely on the single incoming call for verification.'
        },
        {
          summary: 'Limit public voice recordings',
          details: 'Be cautious about posting videos with clear audio of your voice on social media. Adjust privacy settings to limit who can access your content, and consider if voice recordings are necessary for posts. Remember that voicemails can also be a source of voice samples.'
        },
        {
          summary: 'Be skeptical of urgent money requests',
          details: 'Any call demanding immediate money transfer, gift cards, or financial action should trigger suspicion. Legitimate emergencies allow time for verification. Take a moment to think clearly and verify the situation through independent means before acting.'
        },
        {
          summary: 'Use multi-factor authentication beyond voice',
          details: 'Never rely solely on voice verification for important accounts. Enable additional security measures like text messages, authenticator apps, or biometric verification. Contact your bank and service providers to add extra security layers that can\'t be voice-spoofed.'
        },
        {
          summary: 'Hang up and call back on known numbers',
          details: 'Never conduct sensitive business on incoming calls. If your bank, insurance company, or any service provider calls, politely hang up and call them back on the official number from their website or your statements. This ensures you\'re talking to the real organization.'
        }
      ]
    },
    {
      id: 'ai-avatars',
      name: 'AI Avatars',
      icon: User,
      color: 'from-violet-500 to-purple-500',
      description: 'Digital doubles created by AI platforms',
      risks: [
        {
          summary: 'HeyGen clones impersonating you in videos',
          details: 'HeyGen and similar platforms can create realistic AI avatars of you from just a few photos, then make these avatars say anything in videos. Scammers use these to impersonate you in business communications, fake testimonials, or fraudulent video messages to your contacts.'
        },
        {
          summary: 'Synthesia avatars used for fraud',
          details: 'Criminals use professional AI avatar services like Synthesia to create convincing spokesperson videos using your likeness for fake investment schemes, crypto scams, or fraudulent business proposals. These polished videos lend credibility to scams and are hard to distinguish from real endorsements.'
        },
        {
          summary: 'D-ID digital doubles for misinformation',
          details: 'D-ID and other platforms can animate still photos to create talking avatars that spread false information, fake news, or harmful content while appearing to be you. This can damage your reputation and spread misinformation to your network and beyond.'
        },
        {
          summary: 'Professional identity theft via AI spokespersons',
          details: 'Scammers create AI avatars posing as professionals like doctors, lawyers, or financial advisors using stolen photos and credentials. They use these fake personas in video consultations or promotional content to build trust and commit fraud against unsuspecting clients.'
        },
        {
          summary: 'Fake testimonials and endorsements',
          details: 'Companies or individuals create AI avatars using your likeness to provide fake testimonials for products, services, or investments without your consent. These unauthorized endorsements can associate you with scams or products you\'ve never used, damaging your reputation.'
        }
      ],
      protection: [
        {
          summary: 'Monitor for unauthorized use of your likeness',
          details: 'Regularly search for your name and images online using Google Images reverse search, TinEye, or other tools. Set up Google Alerts for your name and monitor social media platforms. Look for videos or content featuring AI avatars that look like you but weren\'t created by you.'
        },
        {
          summary: 'Add disclaimers to legitimate AI avatar content',
          details: 'If you create legitimate AI avatar content for business or personal use, always include clear disclaimers stating it\'s AI-generated. This helps establish a pattern so people can identify unauthorized AI content that lacks these disclaimers as potentially fraudulent.'
        },
        {
          summary: 'Register your authentic content with provenance tools',
          details: 'Use Content Credentials (C2PA standard) to cryptographically sign your legitimate photos and videos before publishing. Adobe\'s Content Authenticity Initiative and the CAI web app let anyone attach a tamper-evident signature to media, creating a verifiable record you can cite in takedown requests or legal disputes involving AI-generated impostors.'
        },
        {
          summary: 'Use unique gestures/phrases in real videos',
          details: 'Develop signature gestures, phrases, or behaviors in your authentic videos that would be difficult for AI to replicate accurately. This could be specific hand movements, personal catchphrases, or unique mannerisms that your contacts can use to verify authenticity.'
        },
        {
          summary: 'Report unauthorized AI clones immediately',
          details: 'If you discover unauthorized AI avatars using your likeness, immediately report them to the platform hosting the content, file DMCA takedown notices, and document everything for potential legal action. Quick action can prevent wider distribution and minimize damage.'
        },
        {
          summary: 'Search for unauthorized commercial use of likeness',
          details: 'Regularly check if your image is being used in commercial contexts without permission by searching major e-commerce sites, advertising platforms, and business websites. Many unauthorized AI avatars are used for commercial gain, which may violate right of publicity laws.'
        }
      ]
    },
    {
      id: 'biometric-spoofing',
      name: 'Biometric Spoofing',
      icon: Fingerprint,
      color: 'from-red-500 to-rose-500',
      description: 'Bypassing fingerprint, face, and voice security',
      risks: [
        {
          summary: 'Face ID bypass using photos or masks',
          details: 'Attackers can fool facial recognition systems using high-resolution photos, 3D masks, or even video replays of your face. Some systems can be tricked with printed photos held up to cameras, while more sophisticated attacks use silicone masks or deepfake video streams to gain unauthorized access.'
        },
        {
          summary: 'Fingerprint cloning from surfaces',
          details: 'Your fingerprints can be lifted from surfaces you touch and recreated using gelatin, silicone, or 3D printing. Criminals can collect prints from glasses, phones, or public surfaces, then create fake fingerprints to unlock your devices or access secure areas.'
        },
        {
          summary: 'Voice authentication defeat with AI',
          details: 'AI voice cloning can now bypass voice recognition systems used by banks and secure services. Attackers record your voice from calls, videos, or voicemails, then use AI to generate speech that matches your vocal patterns and fools authentication systems.'
        },
        {
          summary: 'Iris scan spoofing with high-res images',
          details: 'High-resolution photos of your eyes can be used to create fake irises using contact lenses or printed images. Some iris scanning systems can be fooled by detailed photographs, especially if they lack proper liveness detection to verify a living eye.'
        },
        {
          summary: '3D-printed fingerprints from photos',
          details: 'Researchers have shown that fingerprints can be recreated from high-resolution photos showing your fingers, even when the prints aren\'t the focus of the image. These 3D-printed replicas can unlock phones and fool fingerprint scanners with surprising accuracy.'
        },
        {
          summary: 'Mask attacks on facial recognition systems',
          details: 'Sophisticated silicone masks or even paper masks can sometimes fool facial recognition systems, especially older or less secure implementations. Some attacks use infrared projectors or specially designed patterns to confuse the algorithms used in face detection.'
        }
      ],
      protection: [
        {
          summary: 'Enable liveness detection where available',
          details: 'Use biometric systems that require proof of life, such as blinking for facial recognition, movement detection for fingerprints, or speaking a random phrase for voice authentication. These features make it much harder for attackers to use static replicas of your biometrics.'
        },
        {
          summary: 'Use multi-factor authentication always',
          details: 'Never rely on biometrics alone for important accounts. Combine fingerprint or face recognition with something you know (password/PIN) and something you have (phone/token). This layered approach ensures that even if one factor is compromised, your account remains secure.'
        },
        {
          summary: 'Avoid biometric-only security for sensitive accounts',
          details: 'For banking, investment, or other critical accounts, require additional verification beyond biometrics. Use traditional passwords, security keys, or SMS codes as backup methods. Biometrics should enhance security, not replace proven authentication methods entirely.'
        },
        {
          summary: 'Clean surfaces after using fingerprint scanners',
          details: 'Wipe down fingerprint scanners after use to remove residual prints that could be collected by attackers. Be especially careful with public scanners at airports, offices, or shared devices. Consider using different fingers for different systems to limit exposure.'
        },
        {
          summary: 'Combine biometrics with PINs or passwords',
          details: 'Set up your devices to require both biometric verification AND a PIN or password for access. This dual requirement makes it exponentially harder for attackers who might bypass one authentication method but would struggle to defeat both simultaneously.'
        },
        {
          summary: 'Cover cameras when not in use',
          details: 'Use privacy covers or tape over cameras on laptops, tablets, and desktop computers when not in use. This prevents unauthorized capture of your face for potential spoofing attacks and also protects against general surveillance through compromised devices.'
        }
      ]
    },
    {
      id: 'social-engineering',
      name: 'Social Engineering',
      icon: Users,
      color: 'from-orange-500 to-red-500',
      description: 'Psychological manipulation for information theft',
      risks: [
        {
          summary: 'Whaling attacks on executives',
          details: 'Cybercriminals target high-value individuals like CEOs, CFOs, and executives with sophisticated scams that appear to come from trusted sources. These attacks often involve extensive research into the target\'s business relationships, recent activities, and communication patterns to create highly convincing fraudulent requests.'
        },
        {
          summary: 'Pretexting for sensitive data',
          details: 'Attackers create elaborate fictional scenarios to trick victims into divulging confidential information. They might pose as IT support, auditors, or vendors, using researched details about your organization to build credibility and manipulate you into sharing passwords, account details, or proprietary information.'
        },
        {
          summary: 'Baiting with malicious downloads',
          details: 'Scammers use curiosity and greed to entice victims into downloading malware. This includes USB drives left in parking lots, fake software updates, "exclusive" documents, or free premium software that actually installs spyware, keyloggers, or ransomware on your device.'
        },
        {
          summary: 'Quid pro quo scams',
          details: 'Attackers offer a service or benefit in exchange for information or access. Common examples include fake IT support offering to "fix" your computer, surveys promising rewards for personal information, or "security tests" that actually compromise your accounts.'
        },
        {
          summary: 'AI-powered spear phishing with personal details',
          details: 'Modern phishing attacks use AI to analyze your social media, professional profiles, and public information to create highly personalized and convincing emails. These messages reference your actual contacts, recent activities, and interests to bypass traditional phishing detection methods.'
        },
        {
          summary: 'LinkedIn fake recruiter targeting',
          details: 'Scammers create fake recruiter profiles on LinkedIn to target professionals with "job opportunities" that require sharing personal information, downloading malicious attachments, or connecting to fraudulent websites. These attacks exploit career ambitions and professional networking trust.'
        }
      ],
      protection: [
        {
          summary: 'Verify identities before sharing info',
          details: 'Always independently verify the identity of anyone requesting sensitive information, even if they seem legitimate. Call the person or organization using official contact information, not numbers provided in the request. Ask specific questions only the real person would know.'
        },
        {
          summary: 'Question unexpected requests',
          details: 'Be suspicious of urgent requests for information, money, or access, especially if they create time pressure or threaten consequences. Legitimate organizations rarely require immediate action and will accommodate verification requests. Take time to think and verify before responding.'
        },
        {
          summary: 'Train on latest phishing tactics',
          details: 'Stay informed about current social engineering trends through security awareness training, industry newsletters, and reliable cybersecurity sources. Practice identifying phishing emails and participate in simulated phishing exercises to build your detection skills and awareness.'
        },
        {
          summary: 'Implement zero-trust policies',
          details: 'Adopt a "never trust, always verify" approach to information sharing and system access. Require verification for all requests regardless of apparent source, limit information sharing on a need-to-know basis, and establish clear protocols for handling sensitive requests.'
        },
        {
          summary: 'Regular security awareness training',
          details: 'Participate in ongoing cybersecurity education that covers the latest social engineering techniques, real-world examples, and best practices. Make security training interactive and relevant to your specific role and industry to maximize retention and practical application.'
        },
        {
          summary: 'Verify requests through official company channels',
          details: 'When receiving requests that appear to be from colleagues, vendors, or partners, verify them through official company communication channels. Use internal directories, official email addresses, or established communication protocols rather than responding directly to potentially compromised accounts.'
        }
      ]
    },
    {
      id: 'data-brokers',
      name: 'Data Brokers',
      icon: Database,
      color: 'from-green-500 to-emerald-500',
      description: 'Companies collecting and selling personal data',
      risks: [
        {
          summary: 'Identity theft from aggregated data',
          details: 'Data brokers compile comprehensive profiles from multiple sources including public records, social media, purchase history, and online activity. When this aggregated data is breached or sold to criminals, it provides everything needed for identity theft, synthetic identity creation, or account takeover attacks.'
        },
        {
          summary: 'Targeted scams using personal info',
          details: 'Scammers purchase detailed profiles from data brokers to create highly convincing phishing attacks, romance scams, or investment frauds. Having access to your shopping habits, family details, financial status, and interests allows them to craft personalized scams that are much harder to detect.'
        },
        {
          summary: 'Privacy invasion and stalking',
          details: 'Data brokers often sell location data, family information, and personal details that can enable stalking, harassment, or physical threats. This information can reveal your daily routines, workplace, family members\' details, and other sensitive information that compromises your personal safety.'
        },
        {
          summary: 'Discriminatory profiling',
          details: 'Data brokers create profiles used for discriminatory practices in employment, housing, insurance, and lending decisions. These profiles may include inferred information about your health, lifestyle, political views, or financial status that could be used to unfairly exclude you from opportunities.'
        },
        {
          summary: 'Real estate and financial targeting from data profiles',
          details: 'Detailed financial profiles are used to target you with predatory lending, investment scams, or real estate fraud. Data brokers sell information about your income, property ownership, debt levels, and spending patterns to companies that may exploit this information for financial gain.'
        }
      ],
      protection: [
        {
          summary: 'Opt out of data broker databases',
          details: 'Manually opt out of major data brokers like Acxiom, LexisNexis, Spokeo, WhitePages, and others by visiting their websites and following their removal processes. This is time-consuming but free, and needs to be repeated regularly as new brokers emerge and old ones may re-add your information.'
        },
        {
          summary: 'Use privacy-focused services',
          details: 'Choose services and products from companies that prioritize privacy and limit data collection. Use search engines like DuckDuckGo, browsers with privacy features, encrypted messaging apps, and avoid loyalty programs that track your purchases and behavior patterns.'
        },
        {
          summary: 'Minimize social media sharing',
          details: 'Reduce the personal information you share on social media platforms, as this data is often sold to data brokers. Adjust privacy settings to limit public access, avoid posting location data, personal details, family information, or other sensitive content that could be aggregated into profiles.'
        },
        {
          summary: 'Regular Google yourself checks',
          details: 'Regularly search for your name, phone number, email address, and other personal identifiers to see what information is publicly available. Set up Google Alerts for your name to monitor when new information about you appears online, and take action to remove problematic content.'
        },
        {
          summary: 'Use data removal services',
          details: 'Consider paid services that automatically opt you out of data broker databases and monitor for new listings. These services handle the tedious process of contacting multiple brokers and can be more effective than manual opt-outs, though they require ongoing subscription fees.'
        },
        {
          summary: 'Use services like DeleteMe or Optery',
          details: 'Professional data removal services like DeleteMe, Optery, or Privacy Bee specialize in removing your information from data broker databases. They typically charge $100-300 per year but can remove your data from hundreds of brokers and provide ongoing monitoring and removal services.'
        },
        {
          summary: 'Freeze credit reports proactively',
          details: 'Place security freezes on your credit reports with all three major credit bureaus (Experian, Equifax, TransUnion) to prevent new accounts from being opened without your permission. This limits how data brokers can verify and sell your financial information to potential creditors or scammers.'
        }
      ]
    },
    {
      id: 'social-media-ai',
      name: 'Social Media AI',
      icon: Smartphone,
      color: 'from-indigo-500 to-purple-500',
      description: 'AI training on your personal content',
      risks: [
        {
          summary: 'Behavioral prediction and manipulation',
          details: 'Social media platforms use AI to analyze your behavior patterns, emotional responses, and engagement data to predict and influence your future actions. This can be used to manipulate your purchasing decisions, political views, or emotional state through targeted content and advertisements.'
        },
        {
          summary: 'Synthetic content using your likeness',
          details: 'AI systems trained on your social media photos and videos can generate fake content featuring your likeness without permission. This synthetic content can be used for harassment, misinformation campaigns, or commercial purposes, often spreading faster than you can identify and remove it.'
        },
        {
          summary: 'Privacy erosion through AI analysis',
          details: 'AI algorithms analyze your posts, comments, likes, and even the time you spend viewing content to infer sensitive information about your health, financial status, sexual orientation, political beliefs, and personal relationships that you never explicitly shared.'
        },
        {
          summary: 'Targeted misinformation',
          details: 'AI systems create personalized misinformation campaigns designed specifically to exploit your psychological profile, interests, and vulnerabilities. These targeted false narratives are more effective because they align with your existing beliefs and concerns, making them harder to recognize as fake.'
        },
        {
          summary: 'Mental health profiling from posts',
          details: 'AI analyzes your social media activity to detect signs of depression, anxiety, or other mental health conditions. This information can be used by advertisers to target vulnerable individuals, by employers for discrimination, or by bad actors to exploit people during difficult times.'
        },
        {
          summary: 'Relationship and behavioral predictions',
          details: 'Social media AI predicts your relationship status, sexual orientation, family dynamics, and likely future behaviors based on your online interactions. This information can be used for discrimination, targeting by predators, or manipulation by those who want to exploit your personal relationships.'
        }
      ],
      protection: [
        {
          summary: 'Review and adjust privacy settings',
          details: 'Regularly audit your privacy settings on all social media platforms to limit who can see your content, how your data is used for advertising, and what information is shared with third parties. These settings change frequently, so check them at least quarterly.'
        },
        {
          summary: 'Limit personal information sharing',
          details: 'Be selective about what personal details you share online. Avoid posting information about your location, family, work schedule, financial status, or emotional state. Even seemingly harmless details can be combined by AI to create comprehensive profiles of your life.'
        },
        {
          summary: 'Use platform opt-outs for AI training',
          details: 'Many platforms now offer options to opt out of AI training programs that use your content. Look for settings related to "AI training," "data usage," or "content licensing" and disable them. Be aware that some platforms make these settings difficult to find or frequently reset them.'
        },
        {
          summary: 'Watermark original content with Content Credentials',
          details: 'Before posting photos or videos, attach cryptographic provenance using the C2PA Content Credentials standard — supported by Adobe Photoshop, Lightroom, and the free Content Authenticity web app. This embeds tamper-evident metadata that proves the content\'s origin, making unauthorized AI-generated copies easier to identify and challenge.'
        },
        {
          summary: 'Consider private alternatives',
          details: 'Explore privacy-focused social media platforms like Signal, Mastodon, or other decentralized networks that don\'t rely on AI-driven advertising models. These platforms typically collect less data and give you more control over how your information is used.'
        },
        {
          summary: 'Regular social media detox periods',
          details: 'Take regular breaks from social media to reduce the amount of behavioral data available for AI analysis. These breaks also help you recognize how the platforms influence your thinking and behavior, making you more resistant to manipulation when you return.'
        }
      ]
    },
    {
      id: 'digital-identity',
      name: 'Digital Identity',
      icon: CreditCard,
      color: 'from-cyan-500 to-teal-500',
      description: 'AI-generated synthetic identities and credential theft',
      risks: [
        {
          summary: 'Synthetic identity creation for fraud',
          details: 'Criminals use AI to create completely fake identities by combining real and fabricated personal information. These synthetic identities can pass background checks, obtain credit cards and loans, and build credit histories over time before being used for large-scale fraud that\'s difficult to trace back to real victims.'
        },
        {
          summary: 'AI-generated fake IDs and documents',
          details: 'Advanced AI can create realistic driver\'s licenses, passports, utility bills, and other identity documents that are increasingly difficult to distinguish from legitimate ones. These fake documents can be used to open accounts, pass verification processes, or commit identity theft.'
        },
        {
          summary: 'Credential stuffing with AI automation',
          details: 'AI systems automate large-scale attempts to use stolen username and password combinations across multiple websites. These attacks are more sophisticated than traditional methods, using behavioral analysis to avoid detection and successfully compromise accounts even with some security measures in place.'
        },
        {
          summary: 'Account takeover with behavioral mimicry',
          details: 'AI analyzes your online behavior patterns to mimic how you typically interact with accounts and services. This allows attackers to take over your accounts without triggering security alerts, as the AI can replicate your normal login times, device usage, and interaction patterns.'
        },
        {
          summary: 'Identity marketplace exploitation',
          details: 'Criminal marketplaces use AI to match stolen identity components with potential buyers and to optimize pricing based on the completeness and value of identity packages. This makes identity theft more efficient and profitable for criminals while making it harder for victims to recover.'
        },
        {
          summary: 'Synthetic identity loans and credit applications',
          details: 'AI helps criminals create convincing synthetic identities specifically designed to obtain loans, credit cards, and other financial products. These applications often succeed because they combine real SSNs with fake names and addresses in ways that bypass traditional fraud detection systems.'
        }
      ],
      protection: [
        {
          summary: 'Monitor credit reports and identity services',
          details: 'Regularly check your credit reports from all three bureaus and consider using identity monitoring services that alert you to new accounts, credit inquiries, or suspicious activity. Set up fraud alerts with credit bureaus to require verification before new accounts are opened.'
        },
        {
          summary: 'Use unique credentials for each account',
          details: 'Create strong, unique passwords for every online account and use a reputable password manager to store them securely. Enable two-factor authentication wherever possible, and avoid using the same security questions or answers across multiple sites to prevent credential stuffing attacks.'
        },
        {
          summary: 'Enable account alerts and notifications',
          details: 'Turn on all available security notifications for your financial and important accounts, including login alerts, transaction notifications, and security setting changes. Respond immediately to any unexpected alerts, as early detection can prevent extensive damage from account takeovers.'
        },
        {
          summary: 'Verify identity verification processes',
          details: 'When companies ask you to verify your identity, confirm you\'re dealing with a legitimate organization by contacting them directly through official channels. Be suspicious of unsolicited identity verification requests, as these may be attempts to gather information for synthetic identity creation.'
        },
        {
          summary: 'Report suspicious account activities immediately',
          details: 'If you notice any unauthorized accounts, credit inquiries, or suspicious activity on your existing accounts, report it immediately to the relevant financial institutions, credit bureaus, and law enforcement. Quick action can limit damage and help prevent further fraud.'
        },
        {
          summary: 'Use credit freezes and monitoring services',
          details: 'Place security freezes on your credit files with all three major credit bureaus to prevent new accounts from being opened without your explicit permission. Consider paid monitoring services that provide more comprehensive tracking of your personal information across multiple databases and the dark web.'
        }
      ]
    },
    {
      id: 'dna-biometric',
      name: 'DNA/Biometric Data',
      icon: Dna,
      color: 'from-pink-500 to-rose-500',
      description: 'Genetic privacy and biometric template protection',
      risks: [
        {
          summary: 'DNA profiling for discrimination',
          details: 'Your genetic information can be used to discriminate against you in employment, insurance, housing, or other opportunities based on predispositions to certain health conditions. Even if such discrimination is illegal, it can be difficult to prove when decisions are made based on genetic profiling.'
        },
        {
          summary: 'Genetic privacy breaches at testing companies',
          details: 'DNA testing companies store vast databases of genetic information that are attractive targets for hackers. When breached, this data can reveal not only your health predispositions but also family relationships, ethnic background, and other sensitive personal information that cannot be changed.'
        },
        {
          summary: 'Familial tracking through DNA databases',
          details: 'Your DNA can be used to identify and track family members who never consented to genetic testing. Law enforcement and others can use partial DNA matches to build family trees and locate relatives, potentially compromising the privacy of your entire extended family.'
        },
        {
          summary: 'Biometric template theft and replication',
          details: 'Biometric templates (mathematical representations of your fingerprints, iris scans, etc.) can be stolen from databases and potentially used to create fake biometrics or gain unauthorized access to systems. Unlike passwords, you cannot change your biometric identifiers if they are compromised.'
        },
        {
          summary: 'Insurance and employment discrimination',
          details: 'Genetic information can be used to deny insurance coverage, increase premiums, or make employment decisions based on perceived health risks. This discrimination can affect not only you but also family members who share similar genetic markers, even if they never took a DNA test.'
        },
        {
          summary: 'Third-party DNA sharing without consent',
          details: 'Many DNA testing companies share your genetic data with research institutions, pharmaceutical companies, and other third parties, often with minimal notification or consent. This data sharing can continue indefinitely and may be used for purposes you never anticipated or approved.'
        },
        {
          summary: 'Pharmaceutical targeting based on genetics',
          details: 'Pharmaceutical companies use genetic data to target individuals with specific drugs or treatments based on their genetic profiles. While this can be beneficial medically, it also enables discriminatory pricing or marketing practices based on your genetic predispositions and vulnerabilities.'
        }
      ],
      protection: [
        {
          summary: 'Understand genetic testing privacy policies',
          details: 'Carefully read and understand the privacy policies of any genetic testing service before providing your DNA. Pay special attention to data sharing practices, data retention periods, and your rights to delete your information. Many companies have complex policies that allow extensive data sharing.'
        },
        {
          summary: 'Use pseudonyms for genetic testing',
          details: 'Consider using a pseudonym and separate contact information when submitting DNA samples to limit the ability to connect genetic results to your real identity. However, be aware that genetic information itself can still be used to identify you through familial connections.'
        },
        {
          summary: 'Secure biometric template storage',
          details: 'If you must use biometric systems, choose services that store encrypted biometric templates locally on your device rather than in cloud databases. Avoid systems that store raw biometric data, and regularly update any biometric-enabled devices to patch security vulnerabilities.'
        },
        {
          summary: 'Limit biometric data sharing',
          details: 'Be selective about which systems and services you provide biometric data to. Avoid unnecessary biometric enrollment and regularly review and delete biometric data from services you no longer use. Ask organizations about their biometric data storage and sharing practices before enrollment.'
        },
        {
          summary: 'Monitor for genetic privacy breaches',
          details: 'Stay informed about data breaches at genetic testing companies and biometric service providers. If your genetic or biometric data is compromised, understand that unlike other personal information, this data cannot be changed and may have lifelong implications for you and your family.'
        },
        {
          summary: 'Opt out of DNA database sharing',
          details: 'If you\'ve already taken a DNA test, review your account settings and opt out of research participation, law enforcement database sharing, and third-party data sharing where possible. Request deletion of your data if you no longer want it stored, though complete removal may not always be possible.'
        }
      ]
    },
    {
      id: 'iot-devices',
      name: 'IoT & Smart Devices',
      icon: Wifi,
      color: 'from-emerald-500 to-green-500',
      description: 'Internet of Things security vulnerabilities',
      risks: [
        {
          summary: 'Smart home device surveillance and eavesdropping',
          details: 'Smart speakers, cameras, and other IoT devices can be hacked to spy on your daily activities, conversations, and routines. These devices often have poor security and may transmit data to unauthorized parties, creating detailed profiles of your personal life and habits.'
        },
        {
          summary: 'IoT botnet recruitment for cyberattacks',
          details: 'Unsecured IoT devices can be hijacked and added to botnets used for distributed denial-of-service attacks, cryptocurrency mining, or other malicious activities. Your devices can be used to attack other targets without your knowledge, potentially making you liable for cyber crimes.'
        },
        {
          summary: 'Voice assistant unauthorized recordings',
          details: 'Smart speakers and voice assistants may record conversations even when not activated by wake words, and this audio can be accessed by hackers, employees, or law enforcement. These recordings can reveal intimate details about your relationships, health, finances, and personal activities.'
        },
        {
          summary: 'Smart device hijacking and control',
          details: 'Attackers can take control of smart thermostats, locks, lights, and other connected devices to harass you, facilitate break-ins, or cause physical harm. This includes manipulating security systems, unlocking doors remotely, or creating dangerous conditions with heating and cooling systems.'
        },
        {
          summary: 'Network infiltration through weak IoT devices',
          details: 'Poorly secured IoT devices serve as entry points for attackers to access your entire home network, including computers, phones, and other sensitive devices. Once inside your network, attackers can steal personal files, monitor your internet activity, or install malware on other devices.'
        },
        {
          summary: 'Smart doorbell and security camera hijacking',
          details: 'Hackers can access smart doorbells and security cameras to monitor your comings and goings, identify when you\'re not home, or use the devices to harass you and your visitors. This surveillance can enable burglary, stalking, or other crimes against you and your family.'
        },
        {
          summary: 'Children\'s toy privacy violations',
          details: 'Smart toys and educational devices often collect extensive data about children, including voice recordings, play patterns, and personal information. This data can be misused for targeted advertising, identity theft, or predatory behavior, and children cannot meaningfully consent to this data collection.'
        }
      ],
      protection: [
        {
          summary: 'Segment IoT devices on separate networks',
          details: 'Create a separate wireless network for your IoT devices, isolated from your main network with computers and phones. This prevents compromised IoT devices from accessing your sensitive data and limits the potential damage from security breaches in smart home devices.'
        },
        {
          summary: 'Regular firmware updates for all devices',
          details: 'Enable automatic updates where possible and regularly check for firmware updates for all your IoT devices. Many IoT security vulnerabilities are patched through firmware updates, but many users never install them, leaving devices permanently vulnerable to known exploits.'
        },
        {
          summary: 'Review and adjust device privacy settings',
          details: 'Carefully review the privacy and security settings for each IoT device, disabling unnecessary features like voice recording, location tracking, or data sharing with third parties. Many devices ship with privacy-invasive settings enabled by default.'
        },
        {
          summary: 'Use strong unique passwords for devices',
          details: 'Change default passwords on all IoT devices and use strong, unique passwords for each device. Many IoT devices ship with weak default passwords that are easily guessed or widely known, making them immediate targets for automated attacks.'
        },
        {
          summary: 'Monitor network traffic for unusual activity',
          details: 'Use network monitoring tools or router features to track which devices are communicating with the internet and identify unusual data transfers or connections to suspicious servers. This can help you detect compromised devices or unauthorized data collection.'
        },
        {
          summary: 'Use VPNs for IoT networks',
          details: 'Consider using a VPN router or service that encrypts all traffic from your IoT devices, preventing eavesdropping on your smart home communications. This is especially important for devices that don\'t support encryption or regularly transmit sensitive data.'
        }
      ]
    },
    {
      id: 'ai-content-detection',
      name: 'AI Content Detection',
      icon: Search,
      color: 'from-yellow-500 to-orange-500',
      description: 'Identifying AI-generated synthetic content',
      risks: [
        {
          summary: 'Inability to distinguish real from AI content',
          details: 'As AI-generated text, images, and videos become more sophisticated, it becomes increasingly difficult for humans to identify synthetic content. This erosion of our ability to distinguish real from fake undermines trust in all media and makes us more susceptible to manipulation and misinformation.'
        },
        {
          summary: 'AI-generated misinformation campaigns',
          details: 'Bad actors use AI to create vast amounts of convincing but false content at scale, including fake news articles, social media posts, and multimedia content. These automated misinformation campaigns can influence public opinion, elections, and social movements more effectively than traditional propaganda.'
        },
        {
          summary: 'Synthetic media flooding information spaces',
          details: 'The internet is being flooded with AI-generated content that dilutes authentic human expression and makes it harder to find genuine information. This "synthetic media pollution" can overwhelm fact-checkers and make it nearly impossible to verify the authenticity of online content.'
        },
        {
          summary: 'Manipulation through believable fake content',
          details: 'AI-generated content tailored to your interests and beliefs can be used to manipulate your opinions, emotions, and decisions. This personalized synthetic content is more persuasive than generic propaganda because it appears to align with your existing views and trusted sources.'
        },
        {
          summary: 'Academic and professional integrity issues',
          details: 'AI-generated content in academic and professional settings undermines the integrity of research, education, and professional work. Students and professionals may unknowingly cite AI-generated fake research or present synthetic work as original, damaging institutional credibility and trust.'
        },
        {
          summary: 'Academic cheating with AI-generated work',
          details: 'Students increasingly use AI to generate essays, reports, and other academic work, violating academic integrity policies and undermining the educational process. This cheating is often difficult to detect and prevents students from developing critical thinking and writing skills.'
        },
        {
          summary: 'Legal document AI generation fraud',
          details: 'AI is being used to generate fake legal documents, contracts, court filings, and other official paperwork that can be used in fraud schemes or to manipulate legal proceedings. These synthetic documents can be difficult to verify and may cause significant legal and financial harm.'
        }
      ],
      protection: [
        {
          summary: 'Use AI detection tools and services',
          details: 'Utilize AI detection services like GPTZero, Originality.AI, or Copyleaks to analyze suspicious content for signs of AI generation. These tools analyze writing patterns, image artifacts, and other technical markers that may indicate synthetic content, though they are not 100% reliable.'
        },
        {
          summary: 'Verify sources and cross-reference information',
          details: 'Always verify information through multiple independent sources, especially for important news or claims. Check original sources, look for corroborating evidence from reputable outlets, and be suspicious of content that only appears on one platform or from unknown sources.'
        },
        {
          summary: 'Develop media literacy and critical thinking',
          details: 'Learn to critically evaluate all content you encounter online by questioning sources, checking for logical consistency, looking for emotional manipulation, and considering who benefits from the information. Stay informed about current AI capabilities and common synthetic content patterns.'
        },
        {
          summary: 'Look for AI generation artifacts',
          details: 'Learn to identify common signs of AI-generated content, such as unusual writing patterns, inconsistent details in images, unnatural skin textures in photos, or generic language that lacks personal voice. Keep in mind that these artifacts become less obvious as AI improves.'
        },
        {
          summary: 'Report suspected synthetic content',
          details: 'Report suspected AI-generated misinformation to platform moderators, fact-checking organizations, and relevant authorities. Your reports help improve detection systems and can prevent harmful synthetic content from spreading to others who might be more vulnerable to manipulation.'
        },
        {
          summary: 'Use multiple AI detection tools',
          details: 'Don\'t rely on a single AI detection tool, as they all have limitations and can be fooled by sophisticated AI-generated content. Use multiple detection services and combine their results with your own critical analysis to make more informed judgments about content authenticity.'
        }
      ]
    },
    {
      id: 'algorithmic-manipulation',
      name: 'Algorithmic Manipulation',
      icon: Target,
      color: 'from-purple-500 to-violet-500',
      description: 'AI-driven behavioral and decision manipulation',
      risks: [
        {
          summary: 'Personalized misinformation targeting',
          details: 'AI algorithms create customized false information designed specifically to exploit your psychological profile, interests, and existing beliefs. This personalized misinformation is more effective than generic propaganda because it aligns with your worldview and appears to come from trusted sources within your information ecosystem.'
        },
        {
          summary: 'Addiction algorithms and behavioral control',
          details: 'Social media and app algorithms are designed to maximize your engagement and time spent on platforms, often exploiting psychological vulnerabilities to create addictive behaviors. These algorithms can interfere with your sleep, relationships, productivity, and mental health by hijacking your attention and decision-making processes.'
        },
        {
          summary: 'Decision interference in critical choices',
          details: 'Algorithmic recommendations can subtly influence major life decisions including career choices, relationships, health decisions, and financial investments. These systems may prioritize engagement or profit over your best interests, leading you toward choices that benefit the platform rather than you.'
        },
        {
          summary: 'Echo chamber reinforcement',
          details: 'Recommendation algorithms create filter bubbles that reinforce your existing beliefs and isolate you from diverse perspectives. This algorithmic echo chamber can lead to political polarization, reduced empathy for others, and an inability to understand or engage with different viewpoints constructively.'
        },
        {
          summary: 'Psychological profiling for manipulation',
          details: 'AI systems build detailed psychological profiles based on your online behavior, then use this information to manipulate your emotions, decisions, and actions. This profiling can identify your insecurities, fears, desires, and cognitive biases to make manipulation more effective and harder to resist.'
        },
        {
          summary: 'Political opinion manipulation during elections',
          details: 'Sophisticated algorithmic campaigns target swing voters with personalized political content designed to influence voting behavior. These campaigns use psychological profiling and emotional manipulation to shift political opinions and voting patterns, potentially undermining democratic processes and informed citizen participation.'
        },
        {
          summary: 'Shopping and financial decision influence',
          details: 'E-commerce and financial service algorithms are designed to influence your purchasing decisions and financial choices to maximize company profits. This includes manipulating prices based on your perceived willingness to pay, promoting unnecessary purchases, or steering you toward more profitable financial products.'
        }
      ],
      protection: [
        {
          summary: 'Diversify information sources and platforms',
          details: 'Actively seek out diverse news sources, social media platforms, and information channels that represent different perspectives and viewpoints. Read content from sources that challenge your existing beliefs and avoid getting all your information from algorithm-curated feeds that may create filter bubbles.'
        },
        {
          summary: 'Practice digital wellness and breaks',
          details: 'Take regular breaks from social media and digital platforms to reduce algorithmic influence on your thinking and behavior. Use app timers, digital detox periods, and mindful consumption practices to maintain control over your attention and decision-making processes.'
        },
        {
          summary: 'Understand how algorithms work',
          details: 'Educate yourself about how recommendation algorithms, engagement optimization, and behavioral targeting work. Understanding these systems helps you recognize when you\'re being manipulated and makes you more resistant to algorithmic influence tactics designed to exploit your psychology.'
        },
        {
          summary: 'Use privacy tools to limit profiling',
          details: 'Use privacy tools like ad blockers, tracking protection, VPNs, and privacy-focused browsers to limit the amount of data available for algorithmic profiling. Clear your browsing data regularly and use private/incognito browsing modes to reduce the effectiveness of behavioral targeting.'
        },
        {
          summary: 'Question personalized recommendations',
          details: 'Be skeptical of personalized recommendations and suggestions from algorithms. Ask yourself why you\'re being shown specific content, who benefits from your engagement, and whether the recommendations align with your genuine interests and values rather than the platform\'s profit motives.'
        },
        {
          summary: 'Use private browsing and ad blockers',
          details: 'Browse in private/incognito mode and use ad blockers to reduce tracking and limit the data available for algorithmic profiling. Consider using privacy-focused search engines like DuckDuckGo and browsers like Firefox or Brave that offer enhanced tracking protection by default.'
        }
      ]
    },
    {
      id: 'quantum-threats',
      name: 'Quantum Computing',
      icon: Zap,
      color: 'from-blue-500 to-indigo-500',
      description: 'Present and near-term quantum computing threats to encryption',
      risks: [
        {
          summary: 'Current encryption vulnerabilities to quantum',
          details: 'Quantum computers will eventually be able to break most current encryption methods, including RSA, ECDSA, and other widely-used cryptographic systems. This means that data encrypted today with standard methods may become readable in the future when quantum computers become sufficiently powerful.'
        },
        {
          summary: 'Data harvesting for future quantum decryption',
          details: 'Adversaries are currently collecting encrypted data with the intention of decrypting it once quantum computers become available. This "harvest now, decrypt later" strategy means that sensitive information encrypted today may be exposed in the future, even if it seems secure now.'
        },
        {
          summary: 'Cryptographic infrastructure obsolescence',
          details: 'The entire global cryptographic infrastructure, including digital certificates, secure communications, financial transactions, and government systems, will need to be replaced with quantum-resistant alternatives. This transition will be complex, expensive, and create security vulnerabilities during the changeover period.'
        },
        {
          summary: 'Retroactive data exposure risks',
          details: 'Sensitive data that you consider permanently secured by current encryption may become readable retroactively once quantum computers advance. This includes personal communications, financial records, health information, and other private data that could cause significant harm if exposed years later.'
        },
        {
          summary: 'National security and privacy implications',
          details: 'Quantum computing will fundamentally alter the balance of power in cybersecurity, potentially giving quantum-capable nations significant advantages in intelligence gathering, economic espionage, and cyber warfare. This could affect global privacy rights and individual security regardless of your location or citizenship.'
        },
        {
          summary: 'Healthcare record vulnerabilities',
          details: 'Medical records and genetic information encrypted with current methods will become vulnerable to quantum decryption. This could lead to future discrimination, privacy violations, and misuse of health data that was thought to be permanently protected when originally encrypted.'
        }
      ],
      protection: [
        {
          summary: 'Advocate for post-quantum encryption adoption',
          details: 'NIST finalized post-quantum cryptography standards in August 2024 — FIPS 203 (ML-KEM), FIPS 204 (ML-DSA), and FIPS 205 (SLH-DSA). Ask your employers, banks, and key service providers whether they have a timeline to adopt these standards. The migration is happening now, not someday — organizations that delay are accumulating technical and security debt.'
        },
        {
          summary: 'Classify and protect sensitive long-term data',
          details: 'Identify information that would be harmful if exposed years or decades from now and take extra precautions with this data. Consider whether some sensitive information should be stored at all, and implement additional layers of protection for data that must be preserved long-term.'
        },
        {
          summary: 'Stay informed on quantum developments',
          details: 'Monitor developments in quantum computing and post-quantum cryptography to understand the timeline and implications for your personal and professional security. Follow guidance from security experts, government agencies, and standards organizations about when and how to implement quantum-resistant measures.'
        },
        {
          summary: 'Use forward secrecy where available',
          details: 'Choose communication tools and services that implement perfect forward secrecy, which ensures that even if encryption keys are compromised in the future, past communications remain protected. This limits the retroactive impact of quantum computing on your historical communications.'
        },
        {
          summary: 'Prepare for post-quantum cryptography transition',
          details: 'NIST\'s post-quantum standards (FIPS 203/204/205) are published and migration is underway across government and enterprise. For individuals: choose services that advertise PQC support, especially for long-lived sensitive communications. For organizations: inventory all systems using RSA/ECC, prioritize TLS and VPN upgrades first, and follow CISA\'s PQC migration roadmap published in 2025.'
        },
        {
          summary: 'Prioritize ephemeral communication methods',
          details: 'Use communication tools that automatically delete messages after a set period, reducing the amount of encrypted data available for future quantum decryption. Consider the long-term sensitivity of your communications when choosing between persistent and ephemeral messaging platforms.'
        }
      ]
    },
    {
      id: 'crypto-web3-scams',
      name: 'Crypto/Web3 AI Scams',
      icon: Coins,
      color: 'from-yellow-500 to-amber-500',
      description: 'AI-powered cryptocurrency and Web3 fraud schemes',
      risks: [
        {
          summary: 'AI-generated fake crypto influencers',
          details: 'Scammers create AI avatars posing as successful crypto traders or influencers to promote fake investment schemes, pump-and-dump scams, or fraudulent tokens. These AI personas build large followings with fake trading results and testimonials to lure victims into financial scams.'
        },
        {
          summary: 'Deepfake celebrity crypto endorsements',
          details: 'Criminals use deepfake technology to create videos of celebrities, entrepreneurs, or financial experts endorsing fake cryptocurrency projects or investment platforms. These convincing fake endorsements can fool thousands of investors into losing their savings to fraudulent schemes.'
        },
        {
          summary: 'AI-powered phishing for wallet credentials',
          details: 'Sophisticated AI systems create personalized phishing attacks targeting crypto users with fake wallet updates, security alerts, or investment opportunities. These attacks are tailored to each victim\'s crypto activity and can successfully steal private keys, seed phrases, and wallet access credentials.'
        },
        {
          summary: 'Fake AI trading bot scams',
          details: 'Scammers promote fake AI-powered trading bots that promise guaranteed profits in crypto trading. Victims pay for access to these bots or deposit funds for "AI trading," only to lose their money to operators who disappear with the funds or provide worthless software.'
        },
        {
          summary: 'NFT and digital asset counterfeiting',
          details: 'AI tools are used to create fake NFT collections, counterfeit digital art, or unauthorized copies of valuable digital assets. These AI-generated fakes are sold on legitimate marketplaces, often copying the style of famous artists or successful NFT projects to deceive buyers.'
        },
        {
          summary: 'Smart contract and DeFi protocol exploitation',
          details: 'AI is used to identify and exploit vulnerabilities in smart contracts and DeFi protocols more efficiently than manual methods. While some of this is legitimate security research, malicious actors use AI to find exploits faster and automate attacks on vulnerable protocols.'
        },
        {
          summary: 'AI-generated whitepaper and project fraud',
          details: 'Scammers use AI to generate professional-looking whitepapers, project documentation, and technical analysis for fake cryptocurrency projects. These AI-generated materials can be convincing enough to attract investment from people who don\'t have the technical expertise to identify the fraud.'
        }
      ],
      protection: [
        {
          summary: 'Verify influencer and celebrity authenticity',
          details: 'Always verify crypto investment advice through official channels and multiple sources. Check for verified social media accounts, cross-reference endorsements with official websites, and be extremely suspicious of any celebrity or influencer promoting specific crypto investments or "get rich quick" schemes.'
        },
        {
          summary: 'Use hardware wallets and cold storage',
          details: 'Store significant crypto holdings in hardware wallets or cold storage solutions that are offline and immune to AI-powered phishing attacks. Never enter your private keys or seed phrases into online platforms, browser extensions, or software that contacts the internet.'
        },
        {
          summary: 'Research projects thoroughly before investing',
          details: 'Conduct extensive due diligence on any crypto project before investing, including verifying team credentials, checking code audits, reviewing tokenomics, and looking for red flags like anonymous teams, unrealistic promises, or pressure to invest quickly. Use multiple independent sources for research.'
        },
        {
          summary: 'Enable multi-factor authentication everywhere',
          details: 'Use strong two-factor authentication on all crypto-related accounts, including exchanges, wallets, and DeFi platforms. Prefer hardware security keys over SMS or app-based 2FA when possible, as these are more resistant to AI-powered social engineering and SIM swapping attacks.'
        },
        {
          summary: 'Be skeptical of guaranteed returns',
          details: 'Reject any crypto investment opportunity that promises guaranteed returns, claims to be "risk-free," or uses high-pressure sales tactics. Legitimate investments always carry risk, and AI-generated marketing materials often include unrealistic profit projections designed to exploit greed and FOMO.'
        },
        {
          summary: 'Verify NFT and digital asset provenance',
          details: 'Before purchasing NFTs or digital assets, verify their authenticity through official artist channels, check transaction history on blockchain explorers, and look for verification badges on legitimate marketplaces. Be wary of suspiciously cheap prices for high-value collections.'
        },
        {
          summary: 'Stay informed about common scam patterns',
          details: 'Follow reputable crypto security resources, scam alert services, and community warnings to stay updated on the latest AI-powered crypto scams. Join legitimate crypto communities that share scam warnings and always verify information before making investment decisions.'
        }
      ]
    }
  ];

  const currentCategory = categories.find(cat => cat.id === activeCategory);

  const openModal = (content) => {
    setModalContent(content);
    setShowModal(true);
  };

  const RiskCard = ({ risk, index }) => (
    <div 
      className="bg-gray-800/50 p-4 rounded-lg border border-gray-700 hover:border-red-500/50 transition-all cursor-pointer transform hover:scale-105"
      onClick={() => openModal({ type: 'risk', content: risk, category: currentCategory.name })}
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0">
          <AlertTriangle className="w-4 h-4 text-red-400" />
        </div>
        <p className="text-gray-300 text-sm">{typeof risk === 'string' ? risk : risk.summary}</p>
      </div>
    </div>
  );

  const ProtectionCard = ({ protection, index }) => (
    <div 
      className="bg-gray-800/50 p-4 rounded-lg border border-gray-700 hover:border-green-500/50 transition-all cursor-pointer transform hover:scale-105"
      onClick={() => openModal({ type: 'protection', content: protection, category: currentCategory.name })}
    >
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0">
          <Shield className="w-4 h-4 text-green-400" />
        </div>
        <p className="text-gray-300 text-sm">{typeof protection === 'string' ? protection : protection.summary}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
              <Shield className="w-6 h-6" />
            </div>
            <h1 className="text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              AI Security Defense Hub
            </h1>
          </div>
          <p className="text-gray-400 text-lg">Navigate modern cybersecurity threats with confidence</p>
        </div>

        {/* Category Selection */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-10">
          {categories.map((category) => {
            const Icon = category.icon;
            return (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`p-4 rounded-xl transition-all transform hover:scale-105 ${
                  activeCategory === category.id 
                    ? `bg-gradient-to-r ${category.color} shadow-xl` 
                    : 'bg-gray-800 hover:bg-gray-700'
                }`}
              >
                <Icon className="w-8 h-8 mx-auto mb-2" />
                <p className="text-xs font-medium">{category.name}</p>
              </button>
            );
          })}
        </div>

        {/* Current Category Display */}
        {currentCategory && (
          <div className="mb-10">
            <div className={`bg-gradient-to-r ${currentCategory.color} p-1 rounded-2xl`}>
              <div className="bg-gray-900 p-8 rounded-2xl">
                <div className="flex items-center gap-4 mb-4">
                  {React.createElement(currentCategory.icon, { className: "w-10 h-10" })}
                  <div>
                    <h2 className="text-3xl font-bold">{currentCategory.name}</h2>
                    <p className="text-gray-400">{currentCategory.description}</p>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-8 mt-8">
                  {/* Risks Section */}
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                      Key Risks
                    </h3>
                    <div className="space-y-3">
                      {currentCategory.risks.map((risk, index) => (
                        <RiskCard key={index} risk={risk} index={index} />
                      ))}
                    </div>
                  </div>

                  {/* Protection Section */}
                  <div>
                    <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                      <Shield className="w-5 h-5 text-green-400" />
                      Protection Strategies
                    </h3>
                    <div className="space-y-3">
                      {currentCategory.protection.map((protection, index) => (
                        <ProtectionCard key={index} protection={protection} index={index} />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Tips */}
        <div className="bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Info className="w-5 h-5 text-blue-400" />
            Universal Protection Tips
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-blue-400 mt-1" />
              <div>
                <p className="font-medium mb-1">Strong Authentication</p>
                <p className="text-sm text-gray-400">Use unique passwords and 2FA everywhere</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Eye className="w-5 h-5 text-blue-400 mt-1" />
              <div>
                <p className="font-medium mb-1">Stay Skeptical</p>
                <p className="text-sm text-gray-400">Question unusual requests and verify sources</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Shield className="w-5 h-5 text-blue-400 mt-1" />
              <div>
                <p className="font-medium mb-1">Regular Updates</p>
                <p className="text-sm text-gray-400">Keep software updated and stay informed</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Database className="w-5 h-5 text-blue-400 mt-1" />
              <div>
                <p className="font-medium mb-1">Monitor Your Data</p>
                <p className="text-sm text-gray-400">Check credit reports and search yourself online</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Smartphone className="w-5 h-5 text-blue-400 mt-1" />
              <div>
                <p className="font-medium mb-1">Digital Hygiene</p>
                <p className="text-sm text-gray-400">Review privacy settings and limit data sharing</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Users className="w-5 h-5 text-blue-400 mt-1" />
              <div>
                <p className="font-medium mb-1">Emergency Planning</p>
                <p className="text-sm text-gray-400">Have backup contacts and incident response plan</p>
              </div>
            </div>
          </div>
        </div>

        {/* Emergency Action Banner */}
        <div className="mt-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0" />
            <div>
              <p className="font-semibold text-red-400">Think you're being targeted?</p>
              <p className="text-sm text-gray-300 mt-1">
                If you suspect an active attack: 1) Don't panic 2) Don't click any links 3) Verify through official channels 4) Report to authorities if needed
              </p>
            </div>
          </div>
        </div>

        {/* Resources Section */}
        <div className="mt-10 bg-gray-800/50 rounded-xl p-6 border border-gray-700">
          <h3 className="text-xl font-semibold mb-6 flex items-center gap-2">
            <Info className="w-5 h-5 text-blue-400" />
            Report & Get Help
          </h3>
          
          <div className="grid md:grid-cols-4 gap-6">
            {/* Federal Resources */}
            <div>
              <h4 className="font-medium text-blue-400 mb-3">Federal Reporting</h4>
              <div className="space-y-2">
                <a href="https://www.ic3.gov" target="_blank" rel="noopener noreferrer" 
                   className="block text-sm text-gray-300 hover:text-white transition-colors">
                  • FBI IC3 - Internet Crime Complaint Center
                </a>
                <a href="https://www.identitytheft.gov" target="_blank" rel="noopener noreferrer"
                   className="block text-sm text-gray-300 hover:text-white transition-colors">
                  • FTC Identity Theft Report
                </a>
                <a href="https://www.cisa.gov/report" target="_blank" rel="noopener noreferrer"
                   className="block text-sm text-gray-300 hover:text-white transition-colors">
                  • CISA - Cybersecurity Reports
                </a>
              </div>
            </div>

            {/* Immediate Help */}
            <div>
              <h4 className="font-medium text-green-400 mb-3">Immediate Assistance</h4>
              <div className="space-y-2">
                <p className="text-sm text-gray-300">
                  • Financial Fraud: Call your bank immediately
                </p>
                <p className="text-sm text-gray-300">
                  • Identity Theft: Call 1-877-438-4338
                </p>
                <p className="text-sm text-gray-300">
                  • Stalking/Threats: Call 911
                </p>
              </div>
            </div>

            {/* Identity & Financial Protection */}
            <div>
              <h4 className="font-medium text-orange-400 mb-3">Identity & Financial Protection</h4>
              <div className="space-y-2">
                <a href="https://www.experian.com/freeze/center.html" target="_blank" rel="noopener noreferrer"
                   className="block text-sm text-gray-300 hover:text-white transition-colors">
                  • Experian Credit Freeze
                </a>
                <a href="https://www.equifax.com/personal/credit-report-services/" target="_blank" rel="noopener noreferrer"
                   className="block text-sm text-gray-300 hover:text-white transition-colors">
                  • Equifax Credit Monitoring
                </a>
                <a href="https://www.transunion.com/credit-freeze" target="_blank" rel="noopener noreferrer"
                   className="block text-sm text-gray-300 hover:text-white transition-colors">
                  • TransUnion Credit Freeze
                </a>
                <a href="https://www.deleteme.com/" target="_blank" rel="noopener noreferrer"
                   className="block text-sm text-gray-300 hover:text-white transition-colors">
                  • DeleteMe - Data Broker Removal
                </a>
              </div>
            </div>

            {/* AI-Specific Resources */}
            <div>
              <h4 className="font-medium text-purple-400 mb-3">AI Detection & Protection Tools</h4>
              <div className="space-y-2">
                <a href="https://chatgpt.com/g/g-szRnS05Rr-scamwatchhq" target="_blank" rel="noopener noreferrer"
                   className="block text-sm text-gray-300 hover:text-white transition-colors">
                  • ScamWatchHQ GPT Assistant
                </a>
                <a href="https://www.realitydefender.com/" target="_blank" rel="noopener noreferrer"
                   className="block text-sm text-gray-300 hover:text-white transition-colors">
                  • Reality Defender - AI Content Detection
                </a>
                <a href="https://scanner.deepware.ai/" target="_blank" rel="noopener noreferrer"
                   className="block text-sm text-gray-300 hover:text-white transition-colors">
                  • Deepware Scanner - Deepfake Detection
                </a>
                <a href="https://www.truemedia.org/" target="_blank" rel="noopener noreferrer"
                   className="block text-sm text-gray-300 hover:text-white transition-colors">
                  • TrueMedia.org - Deepfake Detection
                </a>
                <a href="https://copyleaks.com/ai-content-detector" target="_blank" rel="noopener noreferrer"
                   className="block text-sm text-gray-300 hover:text-white transition-colors">
                  • Copyleaks AI Content Detector
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Sponsors Section */}
        <div className="mt-10 text-center">
          <p className="text-xs text-gray-500 mb-4">Trusted Resources</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="https://www.scamwatchhq.com/" target="_blank" rel="noopener noreferrer"
               className="text-xs text-gray-400 hover:text-blue-400 transition-colors">
              ScamWatchHQ
            </a>
            <span className="text-gray-600">•</span>
            <a href="https://digitaltwin.compliancehub.wiki/" target="_blank" rel="noopener noreferrer"
               className="text-xs text-gray-400 hover:text-blue-400 transition-colors">
              Digital Twin Risk
            </a>
            <span className="text-gray-600">•</span>
            <a href="https://socmed.myprivacy.blog/" target="_blank" rel="noopener noreferrer"
               className="text-xs text-gray-400 hover:text-blue-400 transition-colors">
              Social Media Privacy
            </a>
            <span className="text-gray-600">•</span>
            <a href="https://influencer.myprivacy.blog/" target="_blank" rel="noopener noreferrer"
               className="text-xs text-gray-400 hover:text-blue-400 transition-colors">
              Influencer Privacy
            </a>
            <span className="text-gray-600">•</span>
            <a href="https://digitalwealthshield.com/" target="_blank" rel="noopener noreferrer"
               className="text-xs text-gray-400 hover:text-blue-400 transition-colors">
              Digital Wealth Shield
            </a>
            <span className="text-gray-600">•</span>
            <a href="https://identityrisk.scamwatchhq.com/" target="_blank" rel="noopener noreferrer"
               className="text-xs text-gray-400 hover:text-blue-400 transition-colors">
              Identity Risk
            </a>
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-12 py-6 border-t border-gray-800">
          <div className="text-center">
            <p className="text-xs text-gray-500">
              © 2026 AI Security Defense Hub | Stay Safe in the Digital Age
            </p>
            <p className="text-xs text-gray-600 mt-2">
              Educational resource for cybersecurity awareness - Not a substitute for professional security advice
            </p>
          </div>
        </footer>
      </div>

      {/* Welcome Modal */}
      {showWelcome && (
        <div className="fixed inset-0 bg-black/85 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 border border-cyan-500/40 rounded-2xl p-8 max-w-lg w-full shadow-2xl shadow-cyan-500/10">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                  Welcome to AI Security Defense Hub
                </h2>
                <p className="text-gray-400 text-sm">Your free guide to AI-powered cyber threats</p>
              </div>
            </div>

            {/* What it does */}
            <p className="text-gray-300 text-sm leading-relaxed mb-6">
              This tool covers <span className="text-cyan-400 font-semibold">14 categories</span> of AI-powered cybersecurity threats — from deepfakes and voice cloning to quantum computing risks and crypto scams. For each threat you'll find real risk scenarios <span className="text-red-400">and</span> actionable protection strategies.
            </p>

            {/* How to use steps */}
            <div className="space-y-3 mb-7">
              <div className="flex items-start gap-3 bg-gray-800/60 rounded-xl p-3">
                <div className="w-6 h-6 rounded-full bg-cyan-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-cyan-400 text-xs font-bold">1</span>
                </div>
                <div>
                  <p className="text-white text-sm font-medium">Pick a threat category</p>
                  <p className="text-gray-400 text-xs mt-0.5">Click any icon in the category grid at the top — Deepfakes, Voice Cloning, IoT, and 11 more.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-gray-800/60 rounded-xl p-3">
                <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-red-400 text-xs font-bold">2</span>
                </div>
                <div>
                  <p className="text-white text-sm font-medium">Explore risks on the left</p>
                  <p className="text-gray-400 text-xs mt-0.5">Each red card is a specific attack or threat. Click it for a full explanation of how it works.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-gray-800/60 rounded-xl p-3">
                <div className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-green-400 text-xs font-bold">3</span>
                </div>
                <div>
                  <p className="text-white text-sm font-medium">Get protection steps on the right</p>
                  <p className="text-gray-400 text-xs mt-0.5">Each green card is a defense strategy. Click it for step-by-step guidance you can apply today.</p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <button
              onClick={dismissWelcome}
              className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 text-white font-semibold py-3 rounded-xl transition-all transform hover:scale-105 shadow-lg shadow-cyan-500/20"
            >
              Start Exploring
            </button>
            <p className="text-center text-xs text-gray-600 mt-3">Free educational resource — no account required</p>
          </div>
        </div>
      )}

      {/* Detail Modal */}
      {showModal && modalContent && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50" onClick={() => setShowModal(false)}>
          <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-semibold flex items-center gap-2">
                {modalContent.type === 'risk' ? (
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                ) : (
                  <Shield className="w-5 h-5 text-green-400" />
                )}
                {modalContent.type === 'risk' ? 'Risk Details' : 'Protection Strategy'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mb-4">
              <p className="text-gray-300 font-medium mb-3">{typeof modalContent.content === 'string' ? modalContent.content : modalContent.content.summary}</p>
              {typeof modalContent.content === 'object' && modalContent.content.details && (
                <p className="text-gray-400 text-sm leading-relaxed">{modalContent.content.details}</p>
              )}
            </div>
            <p className="text-sm text-gray-500">Category: {modalContent.category}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiSecurityTool;